import * as Sentry from '@sentry/react';
import AccountableLogo from 'components/atoms/AccountableLogo';
import DevTools from 'components/organisms/CustomDevTools';
import useAuthContinuation from 'hooks/Authentication/useAuthContinuation';
import useAuthStatus from 'hooks/Authentication/useAuthStatus';
import { useInitializeIntercom } from 'hooks/intercom.hook';
import useAssignGrowthABGroups from 'hooks/useAssignGrowthABGroups';
import useLogAmplitudeOnPageVisits from 'hooks/useLogAmplitudeOnPageVisits';
import { type FC, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import OnlyBeforeAuthRoute from 'routes/OnlyBeforeAuthRoute';
import PrivateRoute from 'routes/PrivateRoute';
import type { RouteType } from '../../types/routing.types';
import routes from './routing/routes';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App: FC = () => {
  const { isAuthenticated, isLoadingAuthData, user } = useAuthStatus();

  const { isReady } = useAssignGrowthABGroups(user);

  useLogAmplitudeOnPageVisits();

  useInitializeIntercom();

  const { isLoading } = useAuthContinuation();

  if (isLoadingAuthData || isLoading || !isReady)
    return (
      <div className="flex flex-1 h-screen items-center justify-center bg-white">
        <AccountableLogo className="w-52" />
      </div>
    );

  // group dropdown-button and free buttons
  // variable size/color icons for text/number inputs, maybe also for dropdowns later
  // custom mask function for text/number inputs
  // build multi examples
  // apply to the system and test

  return (
    <>
      <DevTools />
      <SentryRoutes>
        {routes.map(
          ({
            path,
            Component,
            private: isPrivate,
            onlyBeforeAuth,
          }: RouteType): ReactNode => {
            if (isPrivate) {
              return (
                <Route
                  path={path}
                  element={
                    <PrivateRoute isAuthenticated={isAuthenticated}>
                      {Component}
                    </PrivateRoute>
                  }
                  key={path}
                />
              );
            }
            if (onlyBeforeAuth) {
              return (
                <Route
                  path={path}
                  element={
                    <OnlyBeforeAuthRoute isAuthenticated={isAuthenticated}>
                      {Component}
                    </OnlyBeforeAuthRoute>
                  }
                  key={path}
                />
              );
            }
            return <Route path={path} element={Component} key={path} />;
          },
        )}
      </SentryRoutes>
    </>
  );
};

export default App;
//
